import request from '../../utils/request';
import { Result } from '../../models/general';

/**
 * Module configuration view model from API
 */
export interface ModuleConfigView {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * Page configuration view model from API
 */
export interface PageConfigView {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Input model for updating page configuration
 */
export interface PageConfigInput {
  moduleId: string;
  pagePath: string;
  isEnabled: boolean;
}

/**
 * Company module configuration view model from API (Operator only)
 */
export interface CompanyModuleConfigView {
  companyId: number;
  companyName: string;
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}

/**
 * Input model for updating module configuration
 */
export interface ModuleConfigInput {
  moduleId: string;
  isEnabled: boolean;
  displayOrder?: number;
}

/**
 * Get module configuration for current user's company.
 * Returns empty array on error (show all modules by default).
 */
export async function getModuleConfig(): Promise<ModuleConfigView[]> {
  try {
    const response = await request.get<Result<ModuleConfigView[]>>('navigation/modules');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch module config:', error);
    return []; // Return empty on error (show all modules by default)
  }
}

/**
 * Get module configuration for a specific company (Operator only).
 */
export async function getCompanyModuleConfig(companyId: number): Promise<CompanyModuleConfigView | null> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView>>(
      `navigation/modules/company/${companyId}`
    );
    return response.data?.payload ?? null;
  } catch (error) {
    console.error('Failed to fetch company module config:', error);
    return null;
  }
}

/**
 * Update module configuration for a company (Operator only).
 */
export async function updateCompanyModuleConfig(
  companyId: number,
  modules: ModuleConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/modules/company/${companyId}`, modules);
    return true;
  } catch (error) {
    console.error('Failed to update company module config:', error);
    return false;
  }
}

/**
 * Get all companies' module configs (Operator only).
 */
export async function getAllCompanyModuleConfigs(): Promise<CompanyModuleConfigView[]> {
  try {
    const response = await request.get<Result<CompanyModuleConfigView[]>>('navigation/modules/all');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch all company module configs:', error);
    return [];
  }
}

/**
 * Company picker entry: id and name only.
 *
 * Deliberately NOT `CompanyView` from api/General/Company. The Operator is excluded from company
 * employee data by standing owner decision (CLAUDE.md Role Model), so a cross-tenant picker should
 * carry the narrowest payload that can render it. `navigation/modules/all?summary=true` is that
 * payload: Roles = "Operator" on the server (NavigationController.cs:209), a projection over
 * Companies alone that never touches the config tables, and no employee-scoped column anywhere in
 * the shape.
 */
export type CompanySummary = Pick<CompanyModuleConfigView, 'companyId' | 'companyName'>;

/**
 * Outcome of a company-summaries fetch.
 *
 * 🔴 THE POINT OF THE SHAPE IS THAT `companies: []` IS AMBIGUOUS AND MUST NOT BE THE ONLY RETURN.
 * The previous version of this helper swallowed every failure into `[]`, and its one caller then
 * rendered "No companies found. Only Operators can access this page." — so a 500, a timeout or a
 * dropped connection told an Operator they were not an Operator. `failure` is what separates
 * "the request came back and the list really is empty" from "the request did not come back".
 */
export interface CompanySummariesResult {
  /** Always an array. Empty on failure, and empty is NOT by itself evidence of anything. */
  companies: CompanySummary[];
  /**
   * `null` only when the request completed AND carried no `Result.errors`. Otherwise the reason,
   * for logging and for a caller that wants to show a diagnostic.
   *
   * ⚠️ NOT display copy. It is a server description or a thrown-error message, never localized, so a
   * caller must render its own translated message and keep this for the console/diagnostics.
   */
  failure: string | null;
  /**
   * The TRANSPORT status of a failure, so a caller can tell "you may not do this" (401/403) from
   * "this broke, try again" (5xx, timeout, offline). `null` on success and whenever the status could
   * not be recovered.
   *
   * 🔴 Read from `__httpStatus`, never from the body's own `status`: `Result<TPayload>.Status`
   * defaults to 200 and `Result.Ok<T>()` never sets it, so a body under an HTTP 403 still
   * self-reports 200 (CO-56, requestHandling.ts). ASP.NET's authorization denial is BODILESS, which
   * is exactly the case where the body can tell you nothing.
   */
  status: number | null;
}

/** Transport status of a rejected request, or null. Same contract as `httpStatusOf` in PerformanceSignalsAi.ts. */
function httpStatusOf(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const own = (error as { __httpStatus?: unknown }).__httpStatus;
  if (typeof own === 'number' && Number.isFinite(own)) return own;
  const nested = (error as { response?: { status?: unknown } }).response?.status;
  return typeof nested === 'number' && Number.isFinite(nested) ? nested : null;
}

/**
 * A loggable reason for a rejection.
 *
 * The interceptor rejects with the PARSED BODY (or a synthetic object), not with an `Error`, so
 * `String(error)` would record the literal "[object Object]" — a catch that fires and records
 * nothing usable is the defect this helper exists to avoid.
 */
function describeRejection(error: unknown, status: number | null): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const first = (error as { errors?: Array<{ description?: string }> }).errors?.[0]?.description;
    if (first) return first;
  }
  if (typeof error === 'string' && error) return error;
  return status !== null ? `HTTP ${status}` : 'request rejected with no readable reason';
}

/**
 * Fetch the company list in summary form (Operator only).
 *
 * `Settings/ModuleConfig/index.tsx` carries a private copy of this helper (its own local
 * `getCompanySummaries`, which still returns a bare array and still collapses failure into `[]`).
 * That copy is deliberately left alone: during the go-live freeze a shipped Operator page is better
 * left byte-identical than de-duplicated. De-dupe post-freeze — and when you do, carry THIS shape
 * over, because that page inherits the same defect.
 */
export async function getCompanySummaries(): Promise<CompanySummariesResult> {
  try {
    const response = await request.get<Result<CompanySummary[]>>(
      'navigation/modules/all?summary=true'
    );
    const body = response.data;
    if (!body) {
      // A 2xx with no body is not an empty company list; it is a response we cannot read.
      console.error('Failed to fetch company summaries: empty response body');
      return { companies: [], failure: 'empty response body', status: response.status ?? null };
    }
    if (body.errors?.length) {
      // Key Constraint #4 — errors are checked BEFORE payload. A server that answers 200 with an
      // error list was previously read as "zero companies".
      const detail = body.errors[0]?.description || `status ${body.status}`;
      console.error('Failed to fetch company summaries:', detail);
      return { companies: [], failure: detail, status: response.status ?? null };
    }
    return { companies: body.payload ?? [], failure: null, status: null };
  } catch (error) {
    // 🔴 THE CATCH RECORDS THAT IT FIRED — on the console here, and in the returned `failure`,
    // which is what stops the caller from rendering a failure as an empty list.
    const status = httpStatusOf(error);
    const failure = describeRejection(error, status);
    console.error('Failed to fetch company summaries:', status ?? 'no status', failure, error);
    return { companies: [], failure, status };
  }
}

/**
 * Get page configuration for current user's company.
 * Returns empty array on error (show all pages by default).
 */
export async function getPageConfig(): Promise<PageConfigView[]> {
  try {
    const response = await request.get<Result<PageConfigView[]>>('navigation/pages');
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch page config:', error);
    return []; // Return empty on error (show all pages by default)
  }
}

/**
 * Get page configuration for a specific company (Operator only).
 */
export async function getCompanyPageConfig(companyId: number): Promise<PageConfigView[]> {
  try {
    const response = await request.get<Result<PageConfigView[]>>(
      `navigation/pages/company/${companyId}`
    );
    return response.data?.payload ?? [];
  } catch (error) {
    console.error('Failed to fetch company page config:', error);
    return [];
  }
}

/**
 * Update page configuration for a company (Operator only).
 */
export async function updateCompanyPageConfig(
  companyId: number,
  pages: PageConfigInput[]
): Promise<boolean> {
  try {
    await request.put(`navigation/pages/company/${companyId}`, pages);
    return true;
  } catch (error) {
    console.error('Failed to update company page config:', error);
    return false;
  }
}

/**
 * Reserved module id under which company capabilities persist as page rows. Mirrors how profile tabs
 * reuse this table under PROFILE_TAB_MODULE_ID.
 */
export const CAPABILITIES_MODULE_ID = 'company-capabilities';

export const CAPABILITY_KEYS = [
  'canAuthorEmployees',
  'canAuthorRoles',
  'canAuthorGoals',
  'canAuthorSkills',
  'canAuthorAssets',
] as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/**
 * Shared read of the page-config endpoint for capability consumers.
 *
 * 🔴 This used to keep its OWN promise and drop it as soon as it settled, so every mount of
 * `useCompanyCapabilities` fired a fresh `GET navigation/pages`. On the profile page that is once
 * per TAB SWITCH — each `/profile/:id/<tab>` is a separate route rendering its own `<UserProfile>`
 * wrapper, so the shell unmounts and remounts on every tab — for a response the app already had
 * cached in `getNavConfig`. It now reads that one cache.
 *
 * The "drop on settle so a later mount refetches" it replaced was redundant belt-and-braces:
 * `useCompanyCapabilities` already subscribes to `ModuleConfigUpdated` and calls
 * `clearCapabilityConfig()` on it, which is the same invalidation `PageConfigGuard` uses. So
 * freshness is unchanged; only the refetch-per-mount is gone.
 */
export function getCapabilityConfig(userId: string): Promise<PageConfigView[]> {
  return getNavConfig(userId).then(config => config.pages);
}

/** Drop the cached read. Called on logout and on the ModuleConfigUpdated event. */
export function clearCapabilityConfig(): void {
  clearNavConfig();
}

/* ────────────────────────────────────────────────────────────────────────────────────────────────
   Shared read of the company's module + page config, for consumers that need it on EVERY route
   render (`PageConfigGuard`) rather than once per mount.

   Unlike `getCapabilityConfig` above, this promise is NOT dropped when it settles. The guard runs on
   every navigation, and re-fetching per navigation would put two extra requests on the critical path
   of every page in the app. Config only changes when an Operator saves, and that already broadcasts
   `ModuleConfigUpdated` — which is what `clearNavConfig()` is for.

   Both underlying calls swallow their errors and return `[]`, so a failed fetch caches "no config",
   which every consumer reads as ALL ENABLED. That is the correct direction to fail: a config outage
   must not lock tenants out of pages they are entitled to.
   ──────────────────────────────────────────────────────────────────────────────────────────────── */

export interface NavConfig {
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}

let navConfigRequest: Promise<NavConfig> | null = null;
let navConfigOwner: string | null = null;
let navConfigSnapshot: NavConfig | null = null;

/** Shared, cached read of module + page config. `userId` scopes the cache so a re-login refetches. */
export function getNavConfig(userId: string): Promise<NavConfig> {
  if (navConfigOwner !== userId) {
    navConfigOwner = userId;
    navConfigRequest = null;
    navConfigSnapshot = null;
  }
  if (!navConfigRequest) {
    const pending = Promise.all([getModuleConfig(), getPageConfig()]).then(
      ([modules, pages]) => ({ modules, pages })
    );
    navConfigRequest = pending;
    pending.then(config => {
      // Only publish if this is still the live request; a `clearNavConfig()` racing an in-flight
      // read must not have a stale resolution write the snapshot back afterwards.
      if (navConfigRequest === pending) navConfigSnapshot = config;
    });
  }
  return navConfigRequest;
}

/**
 * The resolved config, or `null` if it has not landed yet. Lets a consumer that mounts AFTER the
 * config resolved render the filtered state on its FIRST paint instead of painting the fail-open
 * "everything" state and correcting a tick later.
 *
 * That correction is what made the profile tab strip visibly flash the full tab list on every tab
 * switch: the shell remounts per tab (each tab is its own route), the hook restarted from
 * `loaded: false`, and `isTabEnabled` answers `true` for everything until it loads.
 *
 * Never resolves a promise, never triggers a fetch: a `null` answer means "ask `getNavConfig`".
 */
export function peekNavConfig(userId: string): NavConfig | null {
  return navConfigOwner === userId ? navConfigSnapshot : null;
}

/** Drop the cached read. Called on logout and on the `ModuleConfigUpdated` broadcast. */
export function clearNavConfig(): void {
  navConfigRequest = null;
  navConfigOwner = null;
  navConfigSnapshot = null;
}
